import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, MarketingUser, isSupabaseConfigured } from '@/lib/supabase';

// Development auto-login flag (DEV ONLY)
const env = (import.meta as any).env || {};
const devAutoLogin =
  env.MODE === 'development' &&
  (env.VITE_DEV_AUTOLOGIN === 'true' || env.VITE_DEV_AUTOLOGIN === '1');
// Only bypass auth in development mode.
const isBypassAuth = devAutoLogin || (env.MODE === 'development' && !isSupabaseConfigured());

interface AuthContextType {
  user: User | null;
  marketingUser: MarketingUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [marketingUser, setMarketingUser] = useState<MarketingUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Development bypass: if Supabase isn't configured, auto-sign in a mock admin
    if (isBypassAuth) {
      const fakeUser = { id: 'dev-user-1', email: 'dev@example.com' } as unknown as User;
      const fakeMarketingUser: MarketingUser = {
        id: fakeUser.id,
        email: 'dev@example.com',
        first_name: 'Dev',
        last_name: 'Admin',
        role: 'marketing_admin',
        company_ids: ['halesac'],
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login_at: new Date().toISOString(),
      };
      setUser(fakeUser);
      setMarketingUser(fakeMarketingUser);
      setLoading(false);
      return;
    }

    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadMarketingUser(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadMarketingUser(session.user.id);
      } else {
        setMarketingUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadMarketingUser = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('marketing_users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setMarketingUser(data);
    } catch (error) {
      console.error('Error loading marketing user:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    // If Supabase isn't configured, immediately sign in with a mock admin
    if (isBypassAuth) {
      const fakeUser = { id: 'dev-user-1', email } as unknown as User;
      const fakeMarketingUser: MarketingUser = {
        id: 'dev-user-1',
        email,
        first_name: 'Dev',
        last_name: 'Admin',
        role: 'marketing_admin',
        company_ids: ['halesac'],
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login_at: new Date().toISOString(),
      };
      setUser(fakeUser);
      setMarketingUser(fakeMarketingUser);
      return;
    }

    // Otherwise require a real Supabase sign-in (no production fallback).
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      if (!data?.user) {
        throw new Error('Sign-in failed: no user returned.');
      }

      // Eagerly set the user and load marketing profile before returning
      setUser(data.user);
      await loadMarketingUser(data.user.id);
    } finally {
      // If loadMarketingUser ran it will set loading=false in finally; otherwise clear here
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (isBypassAuth) {
      setUser(null);
      setMarketingUser(null);
      return;
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        marketingUser,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
